import type { LocalPersona, LocalRow, SeedTables } from '../types/localDb.types'
import { SEED_GROUP, seedId } from './seedId'

/**
 * Seed platform JAL WMS: role, user, privilege, dan registry menu
 * (mt_products → mt_modules → mt_clusters → mt_pages) + capability per role.
 * Sumber role: docs/ba-doc/JAL_04-ROLES-PERMISSIONS.md §1 (JAL-ROLE-40-001…012).
 * Sumber visibilitas page: JAL_04 §2 (matriks visibilitas). Persona: prototype `master.users`.
 */

export const SEEDED_AT = new Date('2026-08-01T08:00:00+07:00').toISOString()

export const seedAudit = { status: 'published', sort: null, user_created: null, date_created: SEEDED_AT }

const ROLE_DEFS = [
  ['admin_wh_rm', 'Admin WH RM'],
  ['operator_wh_rm', 'Operator WH RM'],
  ['wh_rm_supervisor', 'WH RM Supervisor'],
  ['kadiv_wh_rm', 'Kadiv WH RM'],
  ['purchasing', 'Purchasing'],
  ['tim_produksi', 'Tim Produksi'],
  ['admin_wh_sansugi', 'Admin WH Sansugi'],
  ['admin_wh_fg', 'Admin WH FG'],
  ['operator_wh_fg', 'Operator WH FG'],
  ['supervisor_wh_fg', 'Supervisor WH FG'],
  ['kadiv_wh_fg', 'Kadiv WH FG'],
  ['sales_marketing', 'Sales / Marketing']
] as const

export type RoleCode = (typeof ROLE_DEFS)[number][0]

/** `ROLE_NO` = nomor role BA (001…012) yang dipakai matriks visibilitas & TL 12B. */
export const ROLE_NO = Object.fromEntries(ROLE_DEFS.map(([code], index) => [code, index + 1])) as Record<
  RoleCode,
  number
>

export const roleId = (code: RoleCode) => seedId(SEED_GROUP.role, ROLE_NO[code])

const ROLES: LocalRow[] = ROLE_DEFS.map(([code, name], index) => ({
  ...seedAudit,
  id: roleId(code),
  code,
  name,
  trace_code: `JAL-ROLE-40-${String(index + 1).padStart(3, '0')}`
}))

const PERSONA_DEFS: [string, string, string, RoleCode][] = [
  ['yulia', 'Yulia', 'Nur I.S.', 'admin_wh_rm'],
  ['galih', 'Galih', 'Adi', 'wh_rm_supervisor'],
  ['rudi', 'Rudi', 'Setiawan', 'purchasing'],
  ['alex', 'Alex', 'K.', 'kadiv_wh_rm'],
  ['karno', 'Karno', '', 'operator_wh_rm'],
  ['joko', 'Joko', 'Prasetyo', 'tim_produksi'],
  ['agus', 'Agus', 'Salim', 'admin_wh_sansugi'],
  ['ulil', 'Ulil', '', 'admin_wh_fg'],
  ['budi', 'Budi', 'Hartono', 'operator_wh_fg'],
  ['hendra', 'Hendra', 'Saputra', 'supervisor_wh_fg'],
  ['ratna', 'Ratna', 'Kusuma', 'kadiv_wh_fg'],
  ['rina', 'Rina', 'Melati', 'sales_marketing'],
  ['tono', 'Tono', 'Wijaya', 'operator_wh_rm'],
  ['sari', 'Sari', 'Puspita', 'operator_wh_fg']
]

export const USER_IDS = Object.fromEntries(
  PERSONA_DEFS.map(([key], index) => [key, seedId(SEED_GROUP.user, index + 1)])
) as Record<string, string>

export const LOCAL_DB_PERSONAS: LocalPersona[] = PERSONA_DEFS.map(([key, first, last, role]) => ({
  id: USER_IDS[key],
  email: `${key}@jaleca.local`,
  first_name: first,
  last_name: last,
  roleId: roleId(role)
}))

const PRODUCT_ID = seedId(SEED_GROUP.product, 1)
const MODULE_ID = seedId(SEED_GROUP.module, 1)

const CLUSTERS = [
  ['GENERAL', 'Umum & Akses'],
  ['INVENTORY_REPORTS', 'Inventory & Laporan'],
  ['DESTRUCTION_FG', 'Pemusnahan FG'],
  ['DESTRUCTION_RM', 'Pemusnahan RM'],
  ['NG_HANDLING_RM', 'Penanganan NG RM'],
  ['MASTER_DATA', 'Master Data'],
  ['PROCUREMENT', 'Pengadaan — Purchase Request'],
  ['INBOUND_RM', 'Inbound WH RM'],
  ['RETURN_ORDER_RM', 'Return Order RM'],
  ['REQUEST_PRODUCTION', 'Request Production'],
  ['WIP_TRANSFER', 'WIP & Transfer Antar Gudang'],
  ['INBOUND_FG', 'Inbound WH FG'],
  ['OUTBOUND_FG', 'Outbound WH FG'],
  ['MERGE_CONSOLIDATION_FG', 'Merge & Konsolidasi WH FG'],
  ['CUSTOMER_CLAIM', 'Customer Claim FG'],
  ['WAREHOUSE_SETUP_TAT', 'Warehouse Setup (TAT)'],
  ['STOCK_OPNAME', 'Stock Opname']
] as const

const clusterId = (code: (typeof CLUSTERS)[number][0]) =>
  seedId(SEED_GROUP.cluster, CLUSTERS.findIndex(([c]) => c === code) + 1)

/** [kode prototype, nama menu, cluster, url, role yang boleh melihat (nomor BA)]. */
const PAGE_DEFS: [string, string, (typeof CLUSTERS)[number][0], string, number[]][] = [
  ['GEN-01', 'Dashboard Utama', 'GENERAL', 'general/dashboard', [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]],
  ['MST-00', 'Hub Master Data', 'MASTER_DATA', 'master-data', [1, 3, 4, 5, 6, 7, 8, 10, 11, 12]],
  ['MST-01', 'Master SKU FG', 'MASTER_DATA', 'master-data/master-sku-fg', [8, 10, 11]],
  ['MST-02', 'Master Raw Material', 'MASTER_DATA', 'master-data/master-raw-material', [1, 5]],
  ['MST-03', 'Master BOM per SKU', 'MASTER_DATA', 'master-data/master-bom', [1, 6]],
  ['MST-03A', 'Detail Spesifikasi Material', 'MASTER_DATA', 'master-data/mst-03a', [1, 6]],
  ['MST-04', 'Master Supplier', 'MASTER_DATA', 'master-data/master-supplier', [1, 5]],
  ['MST-05', 'Master Customer', 'MASTER_DATA', 'master-data/master-customer', [8, 11, 12]],
  ['MST-06', 'Master Lokasi Gudang', 'MASTER_DATA', 'master-data/master-lokasi', [1, 7, 8, 10, 11]],
  ['MST-07', 'Master Area', 'MASTER_DATA', 'master-data/master-area', [1, 3, 4, 7, 8, 10, 11]],
  ['MST-08', 'Master SLA', 'MASTER_DATA', 'master-data/master-sla', [1, 3, 4, 8, 10, 11]],
  ['MST-09', 'Master Alasan NG', 'MASTER_DATA', 'master-data/master-alasan-ng', [1, 3, 4, 8, 10, 11]],
  ['MST-10', 'Master Pallet Karantina', 'MASTER_DATA', 'master-data/master-pallet', [1, 3, 4]],
  ['MST-11', 'Master Unit', 'MASTER_DATA', 'master-data/master-unit', [1, 3, 4, 5]],
  ['PRC-01', 'Purchase Request', 'PROCUREMENT', 'procurement/daftar-pr', [1, 3, 4, 5]],
  ['PRC-03', 'Approval PR (Purchasing)', 'PROCUREMENT', 'procurement/approval-pr', [1, 3, 4, 5]],
  ['PRC-05', 'Inbound Receiving', 'PROCUREMENT', 'procurement/inbound-receiving', [1, 3, 4, 5]],
  ['IRM-04', 'QC Barang RM (Karantina)', 'INBOUND_RM', 'inbound-rm/qc-scheduler', [1, 3, 4]],
  ['IRM-05', 'QC RM — Operator', 'INBOUND_RM', 'inbound-rm/qc-rm-operator', [1, 2, 3, 4]],
  ['IRM-06', 'Putaway RM - Monitoring', 'INBOUND_RM', 'inbound-rm/putaway-monitoring', [1, 3, 4]],
  ['IRM-07', 'Putaway RM - Operator', 'INBOUND_RM', 'inbound-rm/putaway-rm-operator', [1, 2, 3, 4]],
  ['IRM-08', 'Koreksi Penempatan', 'INBOUND_RM', 'inbound-rm/misplacement-fix', [1, 2, 3, 4]],
  ['RTN-01', 'Return Order RM', 'RETURN_ORDER_RM', 'return-order-rm/daftar-ro', [1, 3, 4, 5]],
  ['RTN-04', 'Pengambilan Barang NG', 'RETURN_ORDER_RM', 'return-order-rm/pengambilan-ng', [1, 2, 3, 4, 5]],
  /** v7.50: satu menu Request Material (REQ-02 dihapus; Detail = REQ-02A di bawah halaman yang sama). */
  ['REQ-01', 'Request Material', 'REQUEST_PRODUCTION', 'request-production/request-material', [1, 3, 4, 6]],
  ['REQ-03', 'Picking RM — Operator', 'REQUEST_PRODUCTION', 'request-production/picking-rm-operator', [1, 2, 3, 4]],
  [
    'REQ-04',
    'Serah Terima ke Produksi',
    'REQUEST_PRODUCTION',
    'request-production/serah-terima-produksi',
    [1, 3, 4, 6]
  ],
  ['REQ-06', 'Rekap Pengambilan', 'REQUEST_PRODUCTION', 'request-production/rekap-pengambilan', [1, 3, 4, 6]],
  ['WIP-01', 'Retur Sisa WIP', 'WIP_TRANSFER', 'wip-transfer/retur-sisa-wip', [1, 3, 4, 6]],
  ['TRF-01', 'Kirim Barang Antar Gudang', 'WIP_TRANSFER', 'wip-transfer/kirim-antar-gudang', [1, 3, 4, 7]],
  ['IFG-01', 'Penerimaan FG (ALM)', 'INBOUND_FG', 'inbound-fg/penerimaan-fg', [8, 10, 11]],
  ['IFG-03', 'Putaway FG — Monitoring', 'INBOUND_FG', 'inbound-fg/putaway-fg-monitor', [8, 10, 11]],
  ['IFG-04', 'Putaway FG — Operator', 'INBOUND_FG', 'inbound-fg/putaway-fg-operator', [8, 9, 10, 11]],
  ['IFG-05', 'Terima & Cek FG — Admin', 'INBOUND_FG', 'inbound-fg/terima-cek-fg', [8, 10, 11]],
  ['OFG-01', 'Order Pengiriman FG', 'OUTBOUND_FG', 'outbound-fg/order-pengiriman', [8, 10, 11, 12]],
  ['OFG-04', 'Picking FG — Operator', 'OUTBOUND_FG', 'outbound-fg/picking-fg-operator', [8, 9, 10, 11]],
  ['OFG-06', 'Return to Stock FG', 'OUTBOUND_FG', 'outbound-fg/return-to-stock-fg', [8, 9, 10, 11]],
  [
    'GAB-01',
    'Barang Lepas FG & Penggabungan',
    'MERGE_CONSOLIDATION_FG',
    'merge-consolidation-fg/barang-lepas-gabung',
    [8, 10, 11]
  ],
  [
    'GAB-02',
    'Penggabungan — Operator',
    'MERGE_CONSOLIDATION_FG',
    'merge-consolidation-fg/penggabungan-operator',
    [8, 9, 10, 11]
  ],
  ['KSL-01', 'Konsolidasi Pallet', 'MERGE_CONSOLIDATION_FG', 'merge-consolidation-fg/konsolidasi-pallet', [8, 10, 11]],
  [
    'KSL-02',
    'Konsolidasi Pallet — Operator',
    'MERGE_CONSOLIDATION_FG',
    'merge-consolidation-fg/konsolidasi-operator',
    [8, 9, 10, 11]
  ],
  ['CLM-01', 'Pendataan & Daftar Claim', 'CUSTOMER_CLAIM', 'customer-claim/daftar-claim', [8, 10, 11, 12]],
  ['CLM-02', 'Detail & Proses Claim', 'CUSTOMER_CLAIM', 'customer-claim/detail-claim', [8, 10, 11, 12]],
  ['CLM-03', 'Claim — Operator', 'CUSTOMER_CLAIM', 'customer-claim/claim-operator', [8, 9, 10, 11]],
  [
    'TAT-01',
    'Pendataan Awal — Sesi & Persetujuan',
    'WAREHOUSE_SETUP_TAT',
    'warehouse-setup-tat/pendataan-awal',
    [1, 3, 4]
  ],
  [
    'TAT-02',
    'Pendataan Awal — Petugas',
    'WAREHOUSE_SETUP_TAT',
    'warehouse-setup-tat/pendataan-awal-petugas',
    [1, 2, 3, 4]
  ],
  [
    'TAT-03',
    'Pindah Material — Daftar & Penugasan',
    'WAREHOUSE_SETUP_TAT',
    'warehouse-setup-tat/pindah-material',
    [1, 3, 4]
  ],
  [
    'TAT-04',
    'Pindah Material — Petugas',
    'WAREHOUSE_SETUP_TAT',
    'warehouse-setup-tat/pindah-material-petugas',
    [1, 2, 3, 4]
  ],
  ['DSP-01', 'Dokumen & Berita Acara Pemusnahan', 'DESTRUCTION_FG', 'destruction-fg/dokumen-pemusnahan', [8, 10, 11]],
  ['DSP-01A', 'Detail Dokumen Pemusnahan', 'DESTRUCTION_FG', 'destruction-fg/detail-pemusnahan', [8, 10]],
  ['DSP-02', 'Pemusnahan — Operator', 'DESTRUCTION_FG', 'destruction-fg/pemusnahan-operator', [8, 9, 10, 11]],
  ['DSR-02', 'Berita Acara Pemusnahan RM', 'DESTRUCTION_RM', 'destruction-rm/berita-acara-pemusnahan', [1, 3, 4]],
  ['DSR-05', 'Lapor Barang NG', 'DESTRUCTION_RM', 'destruction-rm/lapor-barang-ng', [1, 2, 3, 4]],
  ['DSR-03', 'Tugas Pemindahan NG', 'NG_HANDLING_RM', 'ng-handling-rm/tugas-pemindahan-ng', [1, 2, 3, 4]],
  ['INV-01', 'Stok Inventory', 'INVENTORY_REPORTS', 'inventory-reports/stok-inventory', [2, 1, 3, 4, 7, 8, 12, 10, 11]],
  [
    'INV-02',
    'Riwayat Mutasi Stok',
    'INVENTORY_REPORTS',
    'inventory-reports/riwayat-mutasi-stok',
    [1, 3, 4, 7, 8, 10, 11]
  ],
  [
    'INV-03',
    'Dashboard Operasional',
    'INVENTORY_REPORTS',
    'inventory-reports/dashboard-operasional',
    [1, 3, 4, 8, 10, 11]
  ],
  ['OPN-01', 'Daftar Sesi Opname', 'STOCK_OPNAME', 'stock-opname/daftar-sesi', [1, 3, 4, 8, 10, 11]],
  ['OPN-03', 'Opname — Operator', 'STOCK_OPNAME', 'stock-opname/opname-operator', [1, 2, 3, 4, 8, 9, 10, 11]],
  ['OPN-04', 'Discrepancy, Investigasi & Write-off', 'STOCK_OPNAME', 'stock-opname/selisih', [1, 3, 4, 8, 10, 11]],
  ['OPN-06', 'Laporan Stock Opname', 'STOCK_OPNAME', 'stock-opname/laporan', [1, 3, 4, 8, 10, 11]],
  /** GEN-03 (BA JAL-PG-40-002): semua role; ditaruh di akhir daftar supaya id halaman lain tidak bergeser. */
  ['GEN-03', 'Pusat Notifikasi', 'GENERAL', 'general/notifications', [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]]
]

/** Nomor role BA yang boleh MELIHAT tiap halaman (sumber yang sama dengan capability platform). */
export const PAGE_VIEW_ROLES: Record<string, number[]> = Object.fromEntries(
  PAGE_DEFS.map(([code, , , , roles]) => [code, roles])
)

export const PAGE_IDS = Object.fromEntries(
  PAGE_DEFS.map(([code], index) => [code, seedId(SEED_GROUP.page, index + 1)])
) as Record<string, string>

/**
 * Halaman yang tidak tampil di menu (prototype `jal-shell.js`: `hidden: true`) — `type: 'sub-page'` disaring
 * `getNavItems`. Halamannya tetap terdaftar dan bisa dibuka lewat tautan.
 */
const SUB_PAGES = ['MST-03A', 'PRC-03', 'IFG-05', 'DSP-01A']

const PAGES: LocalRow[] = PAGE_DEFS.map(([code, name, cluster, path], index) => ({
  ...seedAudit,
  id: PAGE_IDS[code],
  code,
  name,
  cluster: clusterId(cluster),
  url: `/solutions/wms/${path}`,
  order: index + 1,
  type: SUB_PAGES.includes(code) ? 'sub-page' : 'page',
  icon: null,
  is_external_src: false
}))

/** Role yang boleh menulis per page (create/update); selain itu read-only. */
export const WRITE_ROLES: Record<string, number[]> = {
  'MST-01': [8],
  'MST-03': [1],
  'MST-03A': [1],
  'MST-06': [1, 7, 8, 11],
  'MST-07': [1, 3, 4, 7, 8, 10, 11],
  'MST-08': [1, 3, 4, 8, 10, 11],
  'MST-09': [1, 3, 4, 8, 10, 11],
  'MST-10': [1],
  'MST-11': [1, 3, 4],
  'MST-02': [1],
  'MST-04': [1, 5],
  'MST-05': [8],
  'PRC-01': [1, 3],
  'PRC-03': [5],
  'PRC-05': [1, 3],
  'IRM-04': [1, 3],
  'IRM-05': [2],
  'IRM-07': [2],
  'IRM-08': [1, 2],
  'RTN-01': [1, 3, 4],
  'RTN-04': [2],
  'REQ-01': [1, 3, 4, 6],
  'REQ-03': [2],
  'REQ-04': [6],
  'WIP-01': [1, 6],
  'TRF-01': [1, 7],
  'IFG-01': [8, 10, 11],
  'IFG-03': [8],
  'IFG-04': [9],
  'IFG-05': [8],
  'OFG-01': [8],
  'OFG-04': [9],
  'OFG-06': [9],
  'GAB-01': [8, 10],
  'GAB-02': [9],
  'KSL-01': [8, 11],
  'KSL-02': [9],
  'CLM-01': [8],
  'CLM-02': [8, 10, 11],
  'CLM-03': [9],
  'TAT-01': [1, 3, 4],
  'TAT-02': [2],
  'TAT-03': [1, 3],
  'TAT-04': [2],
  'DSP-01': [8],
  'DSP-01A': [8, 10],
  'DSP-02': [9],
  'DSR-02': [1, 3],
  'DSR-05': [2],
  'DSR-03': [2],
  'INV-01': [1, 3],
  'OPN-01': [1, 3, 8, 10],
  'OPN-03': [2, 9],
  'OPN-04': [1, 4, 8, 11],
  'OPN-06': [1, 8]
}

const CAPABILITIES: LocalRow[] = PAGE_DEFS.flatMap(([code, , , , roles]) =>
  roles.map(roleNo => {
    const canWrite = (WRITE_ROLES[code] ?? []).includes(roleNo)
    return {
      ...seedAudit,
      id: seedId(SEED_GROUP.capability, PAGE_DEFS.findIndex(([c]) => c === code) * 100 + roleNo),
      role: seedId(SEED_GROUP.role, roleNo),
      page: PAGE_IDS[code],
      create: canWrite,
      update: canWrite,
      delete: false
    }
  })
)

export function buildPlatformSeed(): SeedTables {
  return {
    directus_users: LOCAL_DB_PERSONAS.map(persona => ({
      id: persona.id,
      email: persona.email,
      first_name: persona.first_name,
      last_name: persona.last_name,
      status: 'active',
      avatar: null
    })),
    mt_roles: ROLES,
    mt_privileges: LOCAL_DB_PERSONAS.map((persona, index) => ({
      ...seedAudit,
      id: seedId(SEED_GROUP.privilege, index + 1),
      user: persona.id,
      role: persona.roleId
    })),
    mt_products: [
      {
        ...seedAudit,
        id: PRODUCT_ID,
        code: 'WMS',
        name: 'Jaleca WMS',
        description: 'WINVOSYS WMS — PR Jaleca, Plant Kudus',
        order: 1,
        main_page: PAGE_IDS['GEN-01'],
        image: null,
        background_color: null
      }
    ],
    mt_modules: [
      {
        ...seedAudit,
        id: MODULE_ID,
        code: 'WMS',
        name: 'Warehouse Management System',
        product: PRODUCT_ID,
        icon: 'tabler:building-warehouse',
        order: 1
      }
    ],
    mt_clusters: CLUSTERS.map(([code, name], index) => ({
      ...seedAudit,
      id: clusterId(code),
      code,
      name,
      module: MODULE_ID,
      order: index + 1
    })),
    mt_pages: PAGES,
    trx_product_pages: PAGES.map((page, index) => ({
      ...seedAudit,
      id: seedId(SEED_GROUP.productPage, index + 1),
      product: PRODUCT_ID,
      page: page.id
    })),
    mt_capabilities: CAPABILITIES
  }
}
